/**
 * Canvas State Manager - Single Source of Truth
 *
 * Centralized store for all canvas-related state management.
 * Replaces positionCanvasStore and historyStore with a unified approach.
 *
 * Key Features:
 * - Atomic position switching (fixes the offset bug)
 * - Integrated undo/redo history
 * - Automatic preview generation per position
 * - PrintContext stored per position (critical for correct coordinate transformation)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { persistStorage } from '@/lib/impersonation-session';
import { useShallow } from 'zustand/react/shallow';
import type { Canvas as FabricCanvas } from 'fabric';

import {
  serialize,
  deserializeUserObjects,
  toDataURL,
  removeUserObjects,
  applyClipPathToUserObjects,
  hasDesignObjects,
  getUserObjects,
  type CanvasJSON,
} from '@/services/canvas';

import {
  areaFromPrintContext,
  canvasJSONFromPlacementMm,
  canvasJsonFromMm,
  carryOverPlacement,
  carryOverRule,
  computeGeometrySignature,
  computeRelativeDesignStateFromJSON,
  isTargetDrivenRule,
  placementMmToRelative,
  rescaleCanvasObjectsJSON,
  type PlacementMm,
  type CanvasObjectJSON,
  type PrintContextLike,
  type RelativeDesignState,
} from '@designdeck/shared';
import i18n from '@/lib/i18n';

import { adaptCanvasObjects } from '@/utils/canvasObjectAdapter';
import { canvasDebug } from '@/utils/canvasDebugLogger';
import {
  type PrintContext,
  type CreatePrintContextParams,
  createPrintContext,
} from '@/types/printContext';

import { logger } from '@/utils/logger';
import { ensureCanvasFontsLoaded } from '@/utils/canvasFonts';
import { useProductSelectionStore } from './productSelectionStore';
import { useUIStore } from './uiStore';

/**
 * State for a position the user has opened but never designed. `json`
 * stays null so getAllPositionData() and the full save leave the slug
 * alone (see analysis 2026-09-02-004, Befund K).
 */
function createUnmaterializedPositionState(
  printContext: PrintContext,
  productPositionId: string | null
): PositionState {
  return {
    json: null,
    preview: null,
    relativeState: null,
    designCropUrl: null,
    designCropDataUrl: null,
    lastUploadedCropHash: null,
    lastPersistedCropHash: null,
    lastModified: Date.now(),
    printContext,
    productPositionId,
  };
}

// =============================================================================
// TYPES
// =============================================================================

export type PositionKey = 'front' | 'back' | 'back-inside' | string;

/**
 * Reference state for same-position product switches.
 *
 * Switching between products with different print areas is a contain-fit,
 * which is not reversible when applied hop by hop (shirt → mug → shirt would
 * shrink the design). The anchor is the user's last edited state; every
 * switch is computed from it, so peeking at other products never degrades
 * the design. A user edit replaces the anchor.
 */
export interface SwitchAnchor {
  /** Design placement (group bbox, 0-1) on `printContext` -- all a switch needs */
  rel: RelativeDesignState;
  /** Print area the anchor was captured on */
  printContext: PrintContext;
  /** Geometry signature of the canvas state the last switch produced */
  derivedSignature: string;
  /**
   * True when the last switch placed the design by a target-driven rule
   * (percent box, class change, KLEIN, RUNDUM). Such a result cannot be
   * reversed by the relative rule, so an edit made on it must not become
   * the anchor: it becomes a Custom Placement of the product instead
   * (KTD7). The product itself is `printContext.productId`.
   */
  derivedIrreversible?: boolean;
  /** @deprecated Persisted by builds before the carry-over settings. */
  derivedKlein?: boolean;
  /** Product the last switch derived the state for (the focused product) */
  derivedFor?: string;
}

/**
 * State stored per position (front, back, etc.)
 *
 * Stufe 4.3: designCropUrl split into two separate fields to avoid
 * DataURL/S3-URL confusion and enable correct upload lifecycle tracking.
 */
export interface PositionState {
  /** Serialized canvas JSON (user objects only, print-area coordinates) */
  json: CanvasJSON | null;
  /** Preview data URL for ProductCards (full PrintArea export) */
  preview: string | null;
  /** Relative design state for adaptation (0-1 normalized to PrintArea) */
  relativeState: RelativeDesignState | null;
  /** Persisted S3 URL for tight-crop PNG (set from DB on load, updated after upload) */
  designCropUrl: string | null;
  /** Runtime DataURL for tight-crop (fresh from canvas, cleared after successful persist) */
  designCropDataUrl: string | null;
  /** Content hash after last successful S3 upload (for dirty-checking) */
  lastUploadedCropHash: string | null;
  /** Content hash after last successful saveDesignConfiguration (for 2-phase commit) */
  lastPersistedCropHash: string | null;
  /** Last modification timestamp */
  lastModified: number;
  /** PrintContext at time of save - CRITICAL for correct coordinate transformation */
  printContext: PrintContext;
  /** Database ID for ProductPosition - needed for API calls */
  productPositionId: string | null;
  /**
   * Guided entry: preset id, 'custom' for free placement in the guided frame,
   * null or undefined for canvas-authored positions. Persisted so a guided
   * design re-opens with the same preset.
   */
  placementPresetId?: string | null;
  /** Guided entry: design width in mm on the reference Print Area. */
  designWidthMm?: number | null;
}

/**
 * Get the effective crop image source for rendering (MockupRenderer / ProductCard).
 * Prefers fresh local DataURL (immediate after canvas edit),
 * falls back to persisted S3 URL (after reload).
 */
export function getEffectiveCropSrc(pos: PositionState): string | null {
  return pos.designCropDataUrl ?? pos.designCropUrl;
}

/**
 * Per-product design crop data for products with independent canvas designs.
 * Used to display product-specific designs in ProductCard instead of the master design.
 */
export interface ProductDesignCrop {
  cropUrl: string | null;
  relativeState: RelativeDesignState | null;
}

/**
 * History entry for undo/redo
 */
export interface HistoryEntry {
  id: string;
  json: CanvasJSON;
  timestamp: number;
  action: string;
  position: PositionKey;
}

/**
 * Canvas State Manager interface
 */
interface CanvasStateManagerState {
  // ═══════════════════════════════════════════════════════════════════
  // CANVAS REFERENCE
  // ═══════════════════════════════════════════════════════════════════

  /** Current Fabric.js canvas instance */
  canvas: FabricCanvas | null;

  /** Whether canvas is initialized */
  isInitialized: boolean;

  // ═══════════════════════════════════════════════════════════════════
  // POSITION STATE (Level 2: Master + Overrides)
  // ═══════════════════════════════════════════════════════════════════

  /** Master position data — Source of Truth for canvas objects (Stufe 5) */
  masterPositions: Record<PositionKey, PositionState>;

  /**
   * Switch anchors per position (see SwitchAnchor). Kept outside
   * PositionState on purpose: saves and design (re)loads rebuild
   * PositionState, but the anchor must survive them -- a stale anchor is
   * harmless because every switch verifies it via the geometry signature.
   */
  switchAnchors: Record<PositionKey, SwitchAnchor>;

  /** Currently active position slug */
  activePosition: PositionKey;

  /** Currently active product ID (Stufe 5: Per-product overrides) */
  activeProductId: string | null;

  /**
   * Product id of the Master Product of the loaded design (see CONCEPTS.md).
   * The switch anchor rule only turns an edit into a Custom Placement when
   * the focused product is not the master. Null outside the Seller Creator.
   */
  masterProductId: string | null;

  /** Per-product placement states (Stufe 5+) */
  placementsByProduct: Record<string, Record<PositionKey, RelativeDesignState>>;

  /** Per-product independent design data (crop URL + relativeState) */
  productDesignCrops: Record<string, Record<PositionKey, ProductDesignCrop>>;

  /**
   * Current PrintContext (from productSelectionStore); null until a
   * calibrated template of the focused product is active.
   */
  activePrintContext: PrintContext | null;

  /** Flag to prevent saves during position switch */
  isSwitchingPosition: boolean;

  /** Positions the user emptied on purpose; only then may an empty canvas overwrite stored objects */
  userClearedPositions: Partial<Record<PositionKey, boolean>>;

  /** Mark a position as (not) deliberately emptied by the user */
  markPositionCleared: (position: PositionKey, cleared: boolean) => void;

  /** Restore currently writing to the canvas; later restores await it (never persisted) */
  pendingRestore: Promise<boolean> | null;

  // ═══════════════════════════════════════════════════════════════════
  // HISTORY STATE
  // ═══════════════════════════════════════════════════════════════════

  /** Undo stack */
  undoStack: HistoryEntry[];

  /** Redo stack */
  redoStack: HistoryEntry[];

  /** Maximum history entries */
  maxHistory: number;

  /** Flag to prevent history saves during restore */
  isRestoring: boolean;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Canvas Reference
  // ═══════════════════════════════════════════════════════════════════

  /** Set the canvas reference */
  setCanvas: (canvas: FabricCanvas | null) => void;

  /** Mark canvas as initialized */
  setInitialized: (value: boolean) => void;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Position Management
  // ═══════════════════════════════════════════════════════════════════

  /**
   * ATOMIC POSITION SWITCH
   * Saves current position with its PrintContext, then loads new position.
   * This fixes the offset bug by using the CURRENT context for saving.
   */
  /**
   * Switch to another position. Resolves to `true` when the switch actually
   * ran, `false` when it was refused (no canvas, or another switch/design
   * load holds the lock). Callers must not treat a refusal as done — the
   * store state still points at the old position.
   */
  switchPosition: (
    toPosition: PositionKey,
    newContext: PrintContext,
    productPositionId?: string | null
  ) => Promise<boolean>;

  /** Save current canvas state for active position */
  saveCurrentPosition: () => void;

  /**
   * Load saved position data onto the canvas (for remount scenarios).
   * Unlike switchPosition, this loads data for the CURRENT position without saving first.
   * Used when CanvasEditor remounts and needs to restore previously saved objects.
   */
  loadSavedPosition: (
    position: PositionKey,
    printContext: PrintContext
  ) => Promise<boolean>;

  /** Update preview for a position */
  updatePreview: (position: PositionKey) => void;

  /** Get preview for a position */
  getPreview: (position: PositionKey) => string | null;

  /** Check if position has design */
  hasDesignForPosition: (position: PositionKey) => boolean;

  /** Get all position slugs that have designs */
  getPositionsWithDesigns: () => PositionKey[];

  /**
   * Write a position's design state without a mounted Fabric canvas.
   *
   * The canvas-backed writers (`saveCurrentPosition`, `switchPosition`) all read
   * the live canvas and can only serve the active position. A placement picked
   * outside the editor — a preset in the guided entry — has to reach the same
   * state for two independent gates: `getPositionsWithDesigns()` (the step
   * guards) and the save path, which drops positions whose `json` is null.
   *
   * Throws on a degenerate placement rather than storing an object that would
   * count as a design while rendering nothing.
   */
  writePositionFromPlacement: (input: {
    position: PositionKey;
    /** The design box in mm on the print area of `printContext` (R17). */
    placementMm: PlacementMm;
    designCropUrl: string;
    /** Natural size of the image behind designCropUrl. */
    source: { width: number; height: number };
    printContext: PrintContext;
    productPositionId: string | null;
    /** Guided entry: what the seller chose, persisted for re-entry. */
    placementPresetId?: string | null;
    designWidthMm?: number | null;
    /**
     * Fabric custom properties for the synthesized object — an embroidery
     * motif's `embFileKey` and `confirmedMapping`, read by the publish rules.
     */
    objectProps?: Record<string, unknown>;
  }) => void;

  /**
   * Record how a position was authored without touching its geometry —
   * used for a side placed freely in the guided frame, whose objects the
   * canvas writes itself.
   */
  setPositionAuthoring: (
    position: PositionKey,
    authoring: {
      placementPresetId: string | null;
      designWidthMm: number | null;
    }
  ) => void;

  /** Clear position data */
  clearPosition: (position: PositionKey) => void;

  /** Clear all position data */
  clearAllPositions: () => void;

  /**
   * Drop every switch anchor and the master reference. Direct Sale calls
   * this on a product switch: the anchor of the previous product must not
   * carry a Standard design onto a KLEIN product as a maximised one.
   */
  clearSwitchAnchors: () => void;

  /** Remember the design's master product (set by designStore on load/save). */
  setMasterProduct: (productId: string | null) => void;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - History
  // ═══════════════════════════════════════════════════════════════════

  /** Save current state to history */
  saveToHistory: (action: string) => void;

  /** Undo last action */
  undo: () => Promise<void>;

  /** Redo last undone action */
  redo: () => Promise<void>;

  /** Clear history */
  clearHistory: () => void;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Design Loading (from Backend)
  // ═══════════════════════════════════════════════════════════════════

  /** Load position data from saved design and auto-load canvas if available */
  loadFromDesign: (
    positionData: Array<{
      positionSlug: string;
      /** Area-relative canvas JSON in pixels of `printContext`'s template */
      canvasData?: Record<string, unknown>;
      /** Context of the template the JSON was converted for (R18) */
      printContext: CreatePrintContextParams;
      productPositionId?: string | null;
      /** Persisted S3 crop URL from DB (Stufe 4.3) */
      designCropUrl?: string | null;
      /** Guided entry authoring intent, from DB */
      placementPresetId?: string | null;
      designWidthMm?: number | null;
    }>
  ) => Promise<void>;

  /**
   * Get all position canvas data for saving to backend (keyed by position
   * slug). By default the MASTER state is resolved through the switch anchor
   * (KTD8): a position currently shown on another product's print area is
   * handed back in the anchor's coordinate space, so canvasData,
   * relativeState and printContext all describe the master area. An
   * edit made on an irreversibly derived product becomes that product's
   * Custom Placement on the way. Pass `resolveMaster: false` to read the
   * live state as-is (single-product editor).
   */
  getAllPositionData: (options?: { resolveMaster?: boolean }) => Record<
    PositionKey,
    {
      json: CanvasJSON | null;
      printContext: PrintContext;
      relativeState: RelativeDesignState | null;
      /** Effective crop source (DataURL or S3 URL) for save flow */
      designCropUrl: string | null;
      placementPresetId: string | null;
      designWidthMm: number | null;
    }
  >;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Crop Upload State
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Update position state after a successful S3 crop upload (Stufe 4.3).
   * Sets designCropUrl (S3), clears designCropDataUrl, updates hash tracking.
   */
  setCropUploadResult: (
    positionSlug: string,
    s3Url: string,
    contentHash: string
  ) => void;

  /**
   * Mark a position's crop as persisted in the backend (Stufe 4.3).
   * Called after saveDesignConfiguration succeeds.
   */
  setCropPersisted: (positionSlug: string, contentHash: string) => void;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Per-Product Placements (Stufe 5+)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Switch active product (Stufe 5).
   * Sets activeProductId. Master positions stay unchanged — only overrides differ.
   */
  switchProduct: (productId: string) => void;

  /** Persist or clear a per-product placement. null = reset/delete */
  setProductPlacement: (
    productId: string,
    slug: PositionKey,
    placement: RelativeDesignState | null
  ) => void;

  /** Drop every per-product override of one product (crops + placements). */
  clearProductOverrides: (productId: string) => void;

  /**
   * Get placement for a specific product + position.
   * Returns undefined if no placement exists (= use auto-adapted fallback).
   */
  getProductPlacement: (
    productId: string,
    slug: PositionKey
  ) => RelativeDesignState | undefined;

  /**
   * Hydrate per-product placements from a loaded design. Only Custom
   * Placements are hydrated; derived rows are rewritten from the master on
   * every save. The caller turns each row's `placementMm` into the relative
   * view on that product's own area.
   */
  hydrateProductPlacements: (
    products: Array<{
      productId: string;
      positions: Array<{
        slug: string;
        /** Relative view of the row's placementMm on its own area */
        placement: RelativeDesignState | null;
        isCustomPlacement?: boolean;
      }>;
    }>
  ) => void;

  /**
   * Set per-product independent design crop data.
   * Called after loading a design or saving in single-product mode.
   */
  setProductDesignCrop: (
    productId: string,
    slug: PositionKey,
    crop: ProductDesignCrop
  ) => void;

  /** Bulk-set product design crops (e.g. on design load) */
  setAllProductDesignCrops: (
    crops: Record<string, Record<PositionKey, ProductDesignCrop>>
  ) => void;

  /**
   * Get the master position state for the currently active position (Stufe 5).
   * Convenience getter — equivalent to masterPositions[activePosition].
   */
  getActiveMasterPosition: () => PositionState | null;

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS - Reset
  // ═══════════════════════════════════════════════════════════════════

  /** Reset entire store */
  reset: () => void;
}

// =============================================================================
// DESIGN ADAPTATION DATA EXTRACTION
// =============================================================================

/**
 * Extract RelativeDesignState and Design-Crop from the current canvas.
 * Called synchronously with preview generation to avoid race conditions.
 *
 * Returns null values if no user objects exist on the canvas.
 */
function extractDesignAdaptationData(
  canvas: FabricCanvas,
  context: PrintContext
): {
  relativeState: RelativeDesignState | null;
  designCropDataUrl: string | null;
} {
  const userObjects = getUserObjects(canvas);
  if (userObjects.length === 0) {
    return { relativeState: null, designCropDataUrl: null };
  }

  try {
    // Compute combined bounding box of all user objects.
    // Fabric.js getBoundingRect() handles rotation, scale, and groups natively.
    let minLeft = Infinity;
    let minTop = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;

    for (const obj of userObjects) {
      const rect = obj.getBoundingRect();
      minLeft = Math.min(minLeft, rect.left);
      minTop = Math.min(minTop, rect.top);
      maxRight = Math.max(maxRight, rect.left + rect.width);
      maxBottom = Math.max(maxBottom, rect.top + rect.height);
    }

    const bboxWidth = maxRight - minLeft;
    const bboxHeight = maxBottom - minTop;

    if (bboxWidth <= 0 || bboxHeight <= 0) {
      return { relativeState: null, designCropDataUrl: null };
    }

    // The relative view comes from the serialized JSON through the same
    // bounding-box function the save uses (R35), never from the live
    // canvas: a save and a reload must see the same box.
    const areaJson = serialize(canvas, {
      filterSystemLayers: true,
      offset: { x: context.offsetX, y: context.offsetY },
    });
    const relativeState = computeRelativeDesignStateFromJSON(
      { objects: (areaJson.objects ?? []) as CanvasObjectJSON[] },
      {
        widthPx: context.widthPx,
        heightPx: context.heightPx,
        offsetX: 0,
        offsetY: 0,
      }
    );

    // Generate tight-crop of design objects only (transparent background).
    // The existing toDataURL with printAreaOnly=true hides system layers.
    // We temporarily remove canvas background for transparency.
    //
    // The crop feeds every product card, each of which clips to its OWN
    // print area. Export the full artwork: exporting through this product's
    // clipPath would bake this frame's cut into all other products' previews.
    let designCropDataUrl: string | null = null;
    const prevBg = canvas.backgroundColor;
    const prevClips = userObjects.map((obj) => obj.clipPath);
    canvas.backgroundColor = '' as string;
    userObjects.forEach((obj) => {
      obj.clipPath = undefined;
    });
    try {
      // The crop is exported at the size the design happens to have on the
      // canvas, so a motif drawn small yields a small bitmap. Every product
      // card then scales that crop to its own print area -- and on a small
      // area the carry-over maximises it on top of that, which made a
      // deliberately small design look pixelated. Export at a fixed
      // resolution instead, capped so the upload stays small.
      const cropDataUrl = toDataURL(canvas, {
        multiplier: cropMultiplier(bboxWidth, bboxHeight),
        printAreaOnly: true, // Hides system layers (__background__, __mockup__, etc.)
        printArea: {
          x: minLeft,
          y: minTop,
          width: bboxWidth,
          height: bboxHeight,
        },
      });
      designCropDataUrl = cropDataUrl || null;
    } finally {
      canvas.backgroundColor = prevBg;
      userObjects.forEach((obj, i) => {
        obj.clipPath = prevClips[i];
      });
    }

    return { relativeState, designCropDataUrl };
  } catch (error) {
    logger.warn(
      'CanvasStateManager',
      'Failed to extract design adaptation data',
      error instanceof Error ? error : String(error)
    );
    return { relativeState: null, designCropDataUrl: null };
  }
}

/** Tolerance for "same placement" in relative (0-1) units -- well below a pixel. */
const REL_EPSILON = 1e-3;

/** Whether two relative design states describe the same placement. */
function relativeStatesMatch(
  a: RelativeDesignState,
  b: RelativeDesignState
): boolean {
  return (
    Math.abs(a.relCenterX - b.relCenterX) < REL_EPSILON &&
    Math.abs(a.relCenterY - b.relCenterY) < REL_EPSILON &&
    Math.abs(a.relWidth - b.relWidth) < REL_EPSILON &&
    Math.abs(a.designRatio - b.designRatio) < REL_EPSILON
  );
}

/**
 * Whether `area` is the print area the anchor was captured on. Both carry
 * the product id since the carry-over settings, and the id decides: two
 * products with identical pixel areas are different areas. Equal pixel size
 * is the fallback for contexts without an id (persisted anchors of older
 * builds, the default context).
 */
function isAnchorHome(anchor: SwitchAnchor, area: PrintContextLike): boolean {
  const anchorProduct = anchor.printContext.productId;
  const areaProduct = area.productId;
  if (anchorProduct && areaProduct) return anchorProduct === areaProduct;
  return (
    area.widthPx === anchor.printContext.widthPx &&
    area.heightPx === anchor.printContext.heightPx
  );
}

/**
 * Whether a switch from the anchor onto `area` places the design by a
 * target-driven rule (`isTargetDrivenRule`): the result cannot be reversed
 * by the relative rule, so an edit made on it forks into a Custom Placement.
 */
function isIrreversibleSwitch(
  anchor: SwitchAnchor,
  area: PrintContextLike
): boolean {
  if (isAnchorHome(anchor, area)) return false;
  return isTargetDrivenRule(
    carryOverRule(
      areaFromPrintContext(anchor.printContext),
      areaFromPrintContext(area),
      { isMaster: false }
    ).rule
  );
}

/** The irreversible flag, reading anchors persisted by older builds too. */
function derivedIrreversibly(anchor: SwitchAnchor): boolean {
  return anchor.derivedIrreversible === true || anchor.derivedKlein === true;
}

/**
 * Where an anchor lands on an area. Returning to the anchor's own print area
 * is the identity; a foreign area follows `carryOverPlacement`, the one
 * placement rule: percent boxes, class changes, KLEIN and RUNDUM take their
 * placement from the target, everything else keeps relative width and
 * center (a fitting design stays inside, a deliberate overflow stays).
 */
function placementOnArea(
  anchor: SwitchAnchor,
  area: PrintContextLike
): RelativeDesignState {
  return isAnchorHome(anchor, area)
    ? anchor.rel
    : carryOverPlacement(
        anchor.rel,
        areaFromPrintContext(anchor.printContext),
        areaFromPrintContext(area),
        { isMaster: false }
      );
}

/** Long edge a design crop is exported at, in pixels. */
const CROP_TARGET_PX = 1200;
/** Upper bound so a tiny design does not produce a huge upload. */
const CROP_MAX_MULTIPLIER = 8;

/**
 * How much to scale the canvas export so the crop has a usable resolution
 * regardless of how large the design was drawn. A design at 60 px is
 * exported 8x, one that already fills a 1200 px area is exported 1:1.
 */
function cropMultiplier(widthPx: number, heightPx: number): number {
  const longEdge = Math.max(widthPx, heightPx);
  if (!(longEdge > 0)) return 1;
  return Math.min(CROP_MAX_MULTIPLIER, Math.max(1, CROP_TARGET_PX / longEdge));
}

function samePlacement(
  a: RelativeDesignState | undefined,
  b: RelativeDesignState
): boolean {
  const near = (x: number, y: number) => Math.abs(x - y) < 1e-6;
  return (
    a !== undefined &&
    near(a.relCenterX, b.relCenterX) &&
    near(a.relCenterY, b.relCenterY) &&
    near(a.relWidth, b.relWidth) &&
    near(a.designRatio, b.designRatio)
  );
}

/**
 * Record what `resolveAnchorFor` found and hand back the anchor. A geometry
 * edit on an irreversibly derived state belongs to that product alone, so
 * it becomes its Custom Placement and the seller is told (KTD7).
 */
function applyResolvedAnchor(
  resolved: ResolvedAnchor,
  position: string,
  state: {
    placementsByProduct: Record<
      string,
      Record<PositionKey, RelativeDesignState>
    >;
    setProductPlacement: (
      productId: string,
      position: PositionKey,
      placement: RelativeDesignState
    ) => void;
  }
): SwitchAnchor | null {
  const { anchor, forkedEdit, forkedEditProductId } = resolved;
  if (forkedEdit && forkedEditProductId) {
    // The same edit is resolved again on every save and every switch. Only
    // a placement that actually changed is worth writing, and worth telling
    // the seller about.
    const known =
      state.placementsByProduct[forkedEditProductId]?.[position as PositionKey];
    if (!samePlacement(known, forkedEdit)) {
      state.setProductPlacement(
        forkedEditProductId,
        position as PositionKey,
        forkedEdit
      );
      useUIStore
        .getState()
        .showToast(i18n.t('creator.toast.placementOnlyThisProduct'));
      logger.debug(
        'CanvasStateManager',
        `Edit on irreversibly derived product ${forkedEditProductId} recorded as custom placement`,
        { position, placement: forkedEdit }
      );
    }
  }
  return anchor;
}

interface ResolvedAnchor {
  anchor: SwitchAnchor | null;
  /**
   * Set when the user moved, resized or rotated the design on a product
   * whose state a target-driven rule produced (percent box, class change,
   * KLEIN, RUNDUM): the edited placement on the current area, to be
   * recorded as that product's Custom Placement (KTD7). The anchor itself
   * stays untouched in that case.
   */
  forkedEdit: RelativeDesignState | null;
  /** The product the edit belongs to (the one the state was derived for) */
  forkedEditProductId: string | null;
}

/**
 * Resolve the anchor for `objects` (print-area-relative on `currentContext`):
 * reuse it while the design is untouched since the last switch; a user edit
 * makes `objects` the new anchor -- unless the edit was made on an
 * irreversibly derived state of a non-master product, where it becomes a
 * Custom Placement instead.
 *
 * "Untouched" is checked two ways: the exact geometry signature of what the
 * last switch produced, or -- when that signature drifted because the switch
 * result was re-serialized through the DB or a remount -- the objects
 * sitting exactly where the anchor would put them on `currentContext`.
 */
function resolveAnchorFor(
  objects: CanvasObjectJSON[],
  currentContext: PrintContext,
  storedAnchor: SwitchAnchor | undefined,
  masterProductId: string | null
): ResolvedAnchor {
  // Anchors persisted by an older build carried the full json instead of
  // `rel`; treat those as absent.
  const stored = storedAnchor?.rel ? storedAnchor : null;
  const none = { forkedEdit: null, forkedEditProductId: null };
  const signature = computeGeometrySignature(objects);
  if (stored && stored.derivedSignature === signature) {
    return { anchor: stored, ...none };
  }
  const rel = computeRelativeDesignStateFromJSON(
    { objects },
    {
      widthPx: currentContext.widthPx,
      heightPx: currentContext.heightPx,
      offsetX: 0,
      offsetY: 0,
    }
  );
  if (!rel) return { anchor: null, ...none };
  if (
    stored &&
    relativeStatesMatch(rel, placementOnArea(stored, currentContext))
  ) {
    return { anchor: { ...stored, derivedSignature: signature }, ...none };
  }
  // An edit on an irreversibly derived state of a non-master product forks
  // into that product's Custom Placement; only the Seller Creator sets a
  // master here.
  const editedProductId = stored?.derivedFor ?? null;
  if (
    stored &&
    derivedIrreversibly(stored) &&
    masterProductId !== null &&
    editedProductId !== null &&
    editedProductId !== masterProductId
  ) {
    return {
      anchor: stored,
      forkedEdit: rel,
      forkedEditProductId: editedProductId,
    };
  }
  return {
    anchor: { rel, printContext: currentContext, derivedSignature: signature },
    ...none,
  };
}

/**
 * Drop one position from a per-product override map and remove the product key
 * once its last entry is gone. Both override maps (placements and independent
 * crops) are pruned the same way so a reset never leaves one of them behind.
 */
function removeProductPosition<T>(
  map: Record<string, Record<PositionKey, T>>,
  productId: string,
  slug: PositionKey
): Record<string, Record<PositionKey, T>> {
  const entries = map[productId];
  if (!entries) return map;

  const nextEntries = { ...entries };
  delete nextEntries[slug];

  const next = { ...map };
  if (Object.keys(nextEntries).length === 0) {
    delete next[productId];
  } else {
    next[productId] = nextEntries;
  }
  return next;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState = {
  canvas: null,
  isInitialized: false,
  masterPositions: {} as Record<PositionKey, PositionState>,
  switchAnchors: {} as Record<PositionKey, SwitchAnchor>,
  activePosition: 'front' as PositionKey,
  activeProductId: null as string | null,
  masterProductId: null as string | null,
  placementsByProduct: {} as Record<
    string,
    Record<PositionKey, RelativeDesignState>
  >,
  productDesignCrops: {} as Record<
    string,
    Record<PositionKey, ProductDesignCrop>
  >,
  activePrintContext: null as PrintContext | null,
  isSwitchingPosition: false,
  userClearedPositions: {} as Partial<Record<PositionKey, boolean>>,
  pendingRestore: null as Promise<boolean> | null,
  undoStack: [] as HistoryEntry[],
  redoStack: [] as HistoryEntry[],
  maxHistory: 50,
  isRestoring: false,
};

// =============================================================================
// STORE
// =============================================================================

export const useCanvasStateManager = create<CanvasStateManagerState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ═══════════════════════════════════════════════════════════════════
        // CANVAS REFERENCE
        // ═══════════════════════════════════════════════════════════════════

        setCanvas: (canvas) => {
          set({ canvas });
          logger.debug(
            'CanvasStateManager',
            canvas ? 'Canvas set' : 'Canvas cleared'
          );
        },

        setInitialized: (value) => {
          set({ isInitialized: value });
        },

        // ═══════════════════════════════════════════════════════════════════
        // POSITION MANAGEMENT
        // ═══════════════════════════════════════════════════════════════════

        switchPosition: async (
          toPosition,
          newContext,
          productPositionId = null
        ) => {
          if (!get().canvas) {
            logger.warn(
              'CanvasStateManager',
              'Cannot switch position: no canvas'
            );
            return false;
          }

          // A remount restore (loadSavedPosition) may still be adding objects
          // to the canvas. Serializing or clearing now would book those
          // objects to the wrong slug (analysis 2026-09-02-004, section 5).
          const pending = get().pendingRestore;
          if (pending) {
            logger.debug(
              'CanvasStateManager',
              'Waiting for pending restore before switching',
              { toPosition }
            );
            await pending.catch(() => false);
          }

          // The outgoing position's crop is exported below: a text whose
          // face is still loading would be baked in the fallback font.
          const outgoingCanvas = get().canvas;
          const fontsPending =
            outgoingCanvas && ensureCanvasFontsLoaded(outgoingCanvas);
          if (fontsPending) await fontsPending;

          const {
            canvas,
            activePosition,
            masterPositions,
            activePrintContext,
            isSwitchingPosition,
          } = get();

          if (!canvas) {
            logger.warn(
              'CanvasStateManager',
              'Cannot switch position: canvas gone while waiting'
            );
            return false;
          }

          // Prevent concurrent position switches (re-checked after the await)
          if (isSwitchingPosition) {
            logger.debug(
              'CanvasStateManager',
              'Position switch already in progress, skipping'
            );
            return false;
          }

          if (activePosition === toPosition) {
            const currentState = masterPositions[toPosition];
            const oldContext =
              currentState?.printContext ?? activePrintContext ?? newContext;

            // Detect whether the print area actually changed: its pixel
            // geometry, or the product it belongs to. Two products can share
            // a pixel-identical area and still place differently (a percent
            // box on one of them), so the identity counts as a change too.
            const printAreaDimensionsChanged =
              oldContext.widthPx !== newContext.widthPx ||
              oldContext.heightPx !== newContext.heightPx ||
              oldContext.offsetX !== newContext.offsetX ||
              oldContext.offsetY !== newContext.offsetY ||
              (oldContext.productId ?? null) !== (newContext.productId ?? null);

            const storedAnchor = get().switchAnchors[toPosition];
            let nextAnchor: SwitchAnchor | null = storedAnchor?.rel
              ? storedAnchor
              : null;
            let anchorApplied = false;

            // The product this switch derives for: the focused product. An
            // edit made on an irreversibly derived state of a non-master
            // product becomes that product's Custom Placement (KTD7); Direct
            // Sale and the single-product editor never set a master here.
            const { masterProductId } = get();
            const focusedProductId =
              useProductSelectionStore.getState().focusedProductId;

            const placementFromAnchor = (anchor: SwitchAnchor) =>
              placementOnArea(anchor, newContext);

            const resolveAnchor = (
              objects: CanvasObjectJSON[]
            ): SwitchAnchor | null =>
              applyResolvedAnchor(
                resolveAnchorFor(
                  objects,
                  oldContext,
                  storedAnchor,
                  masterProductId
                ),
                toPosition,
                get()
              );

            if (printAreaDimensionsChanged) {
              // Re-fit all user objects into the new print area with ONE scale
              // factor (never distort). The transform is computed from the
              // anchor -- the user's last edited state -- not from the live
              // canvas, so shirt → mug → shirt restores the original exactly.
              const userObjects = getUserObjects(canvas);
              if (userObjects.length > 0) {
                // A multi-selection keeps its children in selection-relative
                // coordinates; the transform below needs absolute ones.
                canvas.discardActiveObject();

                const liveJson = serialize(canvas, {
                  filterSystemLayers: true,
                  offset: { x: oldContext.offsetX, y: oldContext.offsetY },
                });
                const anchor = resolveAnchor(
                  (liveJson.objects ?? []) as CanvasObjectJSON[]
                );

                logger.debug(
                  'CanvasStateManager',
                  'Rescaling objects for same-position print area change',
                  {
                    anchorReused: anchor === storedAnchor,
                    anchorWidth: anchor?.printContext.widthPx,
                    anchorHeight: anchor?.printContext.heightPx,
                    newWidth: newContext.widthPx,
                    newHeight: newContext.heightPx,
                    anchorRel: anchor?.rel,
                    objectCount: userObjects.length,
                  }
                );

                if (anchor) {
                  nextAnchor = anchor;
                  adaptCanvasObjects({
                    canvas,
                    targetContext: newContext,
                    targetPlacement: placementFromAnchor(anchor),
                  });
                  anchorApplied = true;
                }

                canvas.requestRenderAll();
              }

              logger.debug(
                'CanvasStateManager',
                `Same position (${toPosition}) but print area changed, rescaled ${getUserObjects(canvas).length} objects`
              );
            } else {
              logger.debug(
                'CanvasStateManager',
                `Same position (${toPosition}), no print area change detected`,
                {
                  oldWidthPx: oldContext.widthPx,
                  oldHeightPx: oldContext.heightPx,
                  oldOffsetX: oldContext.offsetX,
                  oldOffsetY: oldContext.offsetY,
                  newWidthPx: newContext.widthPx,
                  newHeightPx: newContext.heightPx,
                  newOffsetX: newContext.offsetX,
                  newOffsetY: newContext.offsetY,
                }
              );
            }

            // Re-serialize canvas JSON after rescaling so stored state matches
            // the live canvas objects. Without this, the stored json would have
            // pre-rescale coordinates which causes drift on subsequent loads.
            // IMPORTANT: Only serialize if canvas has user objects. If the canvas
            // is empty (e.g., during remount before design objects are restored),
            // preserve the existing saved JSON to prevent data loss.
            const newOffset = {
              x: newContext.offsetX,
              y: newContext.offsetY,
            };
            const hasUserObjectsOnCanvas = getUserObjects(canvas).length > 0;
            const storedObjects = (currentState?.json?.objects ??
              []) as CanvasObjectJSON[];
            // A deliberate delete leaves the canvas empty on purpose. Without
            // this flag the remount guard below would mistake it for a restore
            // race and re-fit the stored design, resurrecting what the user
            // just removed.
            const positionWasClearedByUser =
              get().userClearedPositions[toPosition] === true;

            let updatedJson: CanvasJSON | null;
            if (!printAreaDimensionsChanged) {
              updatedJson = currentState?.json ?? null;
            } else if (
              hasUserObjectsOnCanvas ||
              storedObjects.length === 0 ||
              positionWasClearedByUser
            ) {
              updatedJson = serialize(canvas, {
                filterSystemLayers: true,
                offset: newOffset,
              });
            } else {
              // Remount race: the position has a design but the canvas has not
              // been restored yet. Serializing now would wipe it. Keep the stored
              // design and move it via the anchor -- exactly like the live path,
              // so a remount never degrades the design -- keeping json and
              // printContext coordinate-consistent.
              logger.warn(
                'CanvasStateManager',
                'Print area changed but canvas has no user objects (likely remount before design restore). Re-fitting stored JSON instead of serializing.',
                {
                  position: toPosition,
                  existingObjectCount: storedObjects.length,
                }
              );
              const anchor = resolveAnchor(storedObjects);
              if (anchor) {
                nextAnchor = anchor;
                anchorApplied = true;
              }
              updatedJson = {
                ...currentState!.json,
                objects: rescaleCanvasObjectsJSON(
                  storedObjects,
                  areaFromPrintContext(oldContext),
                  areaFromPrintContext(newContext),
                  anchor ? placementFromAnchor(anchor) : { isMaster: false }
                ) as CanvasJSON['objects'],
              };
            }

            // The anchor remembers what the switch produced, so the next switch
            // can tell an untouched design from a user edit.
            const updatedAnchor =
              anchorApplied && nextAnchor
                ? {
                    ...nextAnchor,
                    derivedSignature: computeGeometrySignature(
                      (updatedJson?.objects ?? []) as CanvasObjectJSON[]
                    ),
                    // Remember when this switch placed by a target-driven
                    // rule: the next edit here must not move the anchor
                    // (KTD7).
                    derivedIrreversible: isIrreversibleSwitch(
                      nextAnchor,
                      newContext
                    ),
                    ...(focusedProductId
                      ? { derivedFor: focusedProductId }
                      : {}),
                  }
                : nextAnchor;

            // Update context, productPositionId, printContext and json on the position
            const updatedPosition = currentState
              ? {
                  ...currentState,
                  json: updatedJson,
                  printContext: newContext,
                  ...(productPositionId ? { productPositionId } : {}),
                }
              : undefined;

            const nextSwitchAnchors = { ...get().switchAnchors };
            if (updatedAnchor) {
              nextSwitchAnchors[toPosition] = updatedAnchor;
            } else {
              delete nextSwitchAnchors[toPosition];
            }

            set({
              activePrintContext: newContext,
              switchAnchors: nextSwitchAnchors,
              ...(updatedPosition
                ? {
                    masterPositions: {
                      ...masterPositions,
                      [toPosition]: updatedPosition,
                    },
                  }
                : {}),
            });

            // Update preview & adaptation data after rescaling
            if (
              printAreaDimensionsChanged &&
              getUserObjects(canvas).length > 0
            ) {
              get().updatePreview(toPosition);
            }

            return true;
          }

          logger.debug(
            'CanvasStateManager',
            `Switching position: ${activePosition} → ${toPosition}`
          );

          // ═══════════════════════════════════════════════════════════════════
          // 🔍 DEBUG: Position Switch Start
          // ═══════════════════════════════════════════════════════════════════
          canvasDebug.log('StateManager', 'switchPosition START', {
            from: activePosition,
            to: toPosition,
            currentPrintContext: activePrintContext,
            newPrintContext: newContext,
          });

          set({ isSwitchingPosition: true });

          try {
            // ─────────────────────────────────────────────────────────────────
            // 1. SAVE current position with CURRENT PrintContext (CRITICAL!)
            // ─────────────────────────────────────────────────────────────────
            const currentContext =
              masterPositions[activePosition]?.printContext ??
              activePrintContext ??
              newContext;
            const currentPositionId =
              masterPositions[activePosition]?.productPositionId ?? null;
            const currentOffset = {
              x: currentContext.offsetX,
              y: currentContext.offsetY,
            };

            canvasDebug.log('StateManager', 'SAVING current position', {
              position: activePosition,
              offsetUsed: currentOffset,
              printContext: currentContext,
            });

            // Debug: Log canvas objects before serialization
            const canvasObjectsBefore = canvas.getObjects();
            const userObjectsBefore = canvasObjectsBefore.filter(
              (o) => !o.name?.startsWith('__')
            );

            const json = serialize(canvas, {
              filterSystemLayers: true,
              offset: currentOffset,
            });

            const serializedObjectCount = json.objects?.length || 0;
            canvasDebug.log('StateManager', 'Serialized JSON', {
              objectCount: serializedObjectCount,
              objects: json.objects?.map((o, i) => ({
                index: i,
                type: o.type,
                name: o.name,
                left: o.left,
                top: o.top,
              })),
            });

            // CRITICAL FIX: If we have objects on canvas but serialization produced empty array,
            // DO NOT overwrite existing saved state. This preserves previously saved objects.
            const existingPositionState = masterPositions[activePosition];
            const hasExistingSavedObjects =
              existingPositionState?.json &&
              existingPositionState.json.objects &&
              Array.isArray(existingPositionState.json.objects) &&
              existingPositionState.json.objects.length > 0;

            // If serialization failed to preserve objects but we have existing saved state, preserve it
            if (
              userObjectsBefore.length > 0 &&
              serializedObjectCount === 0 &&
              hasExistingSavedObjects &&
              existingPositionState.json &&
              existingPositionState.json.objects
            ) {
              // Use existing saved json instead of empty serialization
              json.objects = existingPositionState.json.objects;
              json.version = existingPositionState.json.version || json.version;
            } else if (
              userObjectsBefore.length > 0 &&
              serializedObjectCount === 0
            ) {
              // Serialization failed and no existing saved state - this is a real error
              logger.error(
                'CanvasStateManager',
                'Serialization lost objects and no existing saved state!',
                {
                  userObjectsBefore: userObjectsBefore.length,
                  serializedObjects: serializedObjectCount,
                  userObjectsDetail: userObjectsBefore.map((o) => ({
                    type: o.type,
                    name: o.name,
                    left: o.left,
                    top: o.top,
                  })),
                }
              );
            }

            // Generate preview before clearing
            const preview = hasDesignObjects(canvas)
              ? toDataURL(canvas, {
                  printAreaOnly: true,
                  printArea: {
                    x: currentContext.offsetX,
                    y: currentContext.offsetY,
                    width: currentContext.widthPx,
                    height: currentContext.heightPx,
                  },
                })
              : null;

            // Extract design adaptation data synchronously with preview
            // (avoids losing relativeState/designCropUrl during position switch)
            const adaptationData = extractDesignAdaptationData(
              canvas,
              currentContext
            );

            // Update masterPositions state - preserve existing productPositionId for current position
            // CRITICAL: Only update if we actually have objects OR if position is empty (don't overwrite saved state with empty)
            // A visited-but-never-designed position (no stored json, no user
            // clear) must not be materialized into `{objects: []}` either
            // (see analysis 2026-09-02-004, Befund K).
            const neverDesigned =
              serializedObjectCount === 0 &&
              (existingPositionState?.json ?? null) === null &&
              get().userClearedPositions[activePosition] !== true;

            const shouldUpdatePosition =
              !neverDesigned &&
              (serializedObjectCount > 0 || // We have objects to save
                get().userClearedPositions[activePosition] === true || // User emptied it on purpose
                !existingPositionState || // No existing state (first time)
                !existingPositionState.json || // No existing json
                !existingPositionState.json.objects || // No existing objects array
                existingPositionState.json.objects.length === 0); // Existing state is already empty

            if (neverDesigned) {
              logger.debug(
                'CanvasStateManager',
                `Keeping ${activePosition} unmaterialized on switch`
              );
            }

            const updatedPositions = {
              ...masterPositions,
              [activePosition]: shouldUpdatePosition
                ? {
                    json,
                    preview,
                    relativeState: adaptationData.relativeState,
                    designCropUrl: existingPositionState?.designCropUrl ?? null,
                    designCropDataUrl: adaptationData.designCropDataUrl,
                    lastUploadedCropHash:
                      existingPositionState?.lastUploadedCropHash ?? null,
                    lastPersistedCropHash:
                      existingPositionState?.lastPersistedCropHash ?? null,
                    lastModified: Date.now(),
                    printContext: currentContext,
                    productPositionId: currentPositionId,
                  }
                : neverDesigned
                  ? {
                      ...createUnmaterializedPositionState(
                        currentContext,
                        currentPositionId
                      ),
                      ...(existingPositionState ?? {}),
                      json: null,
                      lastModified: Date.now(),
                      productPositionId: currentPositionId,
                    }
                  : {
                      // Preserve existing state if we have no objects to save but existing state has objects
                      ...existingPositionState!,
                      preview, // Update preview even if keeping json
                      lastModified: Date.now(),
                      productPositionId: currentPositionId,
                    },
            };

            // ─────────────────────────────────────────────────────────────────
            // 2. CLEAR user objects (keep system layers!)
            // ─────────────────────────────────────────────────────────────────
            removeUserObjects(canvas);

            // ─────────────────────────────────────────────────────────────────
            // 3. LOAD new position with STORED PrintContext (CRITICAL!)
            // ─────────────────────────────────────────────────────────────────
            // Objects were saved in print-area coordinates relative to the STORED PrintContext.
            // We must use the STORED PrintContext's offset to correctly restore them to canvas coordinates.
            // Only use newContext for clipPath (current print area bounds).
            //
            // IMPORTANT: Get the target position state from ORIGINAL masterPositions (before we updated current position)
            // This ensures we load the saved state, not the state we just overwrote
            const toState =
              masterPositions[toPosition] ?? updatedPositions[toPosition];

            // Use stored PrintContext offset if available, otherwise fall back to new context
            const storedContext = toState?.printContext ?? newContext;
            const loadOffset = {
              x: storedContext.offsetX,
              y: storedContext.offsetY,
            };

            canvasDebug.log('StateManager', 'LOADING new position', {
              position: toPosition,
              storedContext: {
                offsetX: storedContext.offsetX,
                offsetY: storedContext.offsetY,
                widthPx: storedContext.widthPx,
                heightPx: storedContext.heightPx,
              },
              newContext: {
                offsetX: newContext.offsetX,
                offsetY: newContext.offsetY,
                widthPx: newContext.widthPx,
                heightPx: newContext.heightPx,
              },
              offsetUsed: loadOffset,
              storedObjectCount: toState?.json?.objects?.length || 0,
              storedObjects: toState?.json?.objects?.map((o, i) => ({
                index: i,
                type: o.type,
                name: o.name,
                left: o.left,
                top: o.top,
              })),
            });

            if (
              toState?.json &&
              toState.json.objects &&
              toState.json.objects.length > 0
            ) {
              // CRITICAL: Use stored PrintContext offset for deserialization
              await deserializeUserObjects(toState.json, canvas, loadOffset);

              // Apply clipPath to loaded objects
              applyClipPathToUserObjects(canvas, {
                x: newContext.offsetX,
                y: newContext.offsetY,
                width: newContext.widthPx,
                height: newContext.heightPx,
              });

              // Log canvas state after loading
              const loadedObjects = canvas
                .getObjects()
                .filter((o) => !o.name?.startsWith('__'));

              logger.debug(
                'CanvasStateManager',
                'After deserialize - Canvas objects',
                {
                  userObjectCount: loadedObjects.length,
                  expectedCount: toState.json.objects.length,
                  objectsMatch:
                    loadedObjects.length === toState.json.objects.length,
                  loadOffsetUsed: loadOffset,
                  storedPrintContext: {
                    offsetX: storedContext.offsetX,
                    offsetY: storedContext.offsetY,
                  },
                  newPrintContext: {
                    offsetX: newContext.offsetX,
                    offsetY: newContext.offsetY,
                  },
                }
              );
            } else {
              logger.debug(
                'CanvasStateManager',
                'No objects to load for position',
                {
                  toPosition,
                  toStateExists: !!toState,
                  toStateJsonExists: !!toState?.json,
                  toStateObjectsExists: !!toState?.json?.objects,
                  toStateObjectsLength: toState?.json?.objects?.length || 0,
                }
              );
            }

            canvas.requestRenderAll();

            // ─────────────────────────────────────────────────────────────────
            // 4. UPDATE state - also set productPositionId for new position
            // ─────────────────────────────────────────────────────────────────
            // CRITICAL FIX: Preserve ALL saved data for target position (json, preview, printContext)
            // Only update productPositionId and metadata, never overwrite saved json/preview
            const existingToState = masterPositions[toPosition];

            // If target position has saved state, preserve it completely
            // Only update productPositionId if provided, keep everything else intact
            const finalPositions: Record<string, PositionState> = {
              ...updatedPositions,
            };

            if (existingToState) {
              // Position has saved state - preserve json, preview, and stored printContext
              // Only update productPositionId if provided
              finalPositions[toPosition] = {
                ...existingToState, // Preserve ALL saved data (json, preview, printContext)
                productPositionId:
                  productPositionId ??
                  existingToState.productPositionId ??
                  null,
                // Keep the stored printContext - it's critical for correct coordinate transformation
                // Don't overwrite with newContext as it might have different offsets
                printContext: existingToState.printContext,
              };
            } else {
              // No saved state exists - create new empty state
              finalPositions[toPosition] = createUnmaterializedPositionState(
                newContext,
                productPositionId ?? null
              );
            }

            set({
              masterPositions: finalPositions,
              activePosition: toPosition,
              activePrintContext: newContext,
              isSwitchingPosition: false,
              // The canvas now holds freshly restored state; a clear that was
              // persisted above no longer needs the flag.
              userClearedPositions: {},
            });

            logger.debug(
              'CanvasStateManager',
              `Position switched to ${toPosition} (positionId: ${productPositionId})`
            );

            logger.debug('CanvasStateManager', 'switchPosition() - END', {
              newActivePosition: toPosition,
              canvasObjectsTotal: canvas.getObjects().length,
              userObjectsCount: canvas
                .getObjects()
                .filter((o) => !o.name?.startsWith('__')).length,
            });

            return true;
          } catch (error) {
            logger.error(
              'CanvasStateManager',
              'Position switch failed:',
              error
            );
            set({ isSwitchingPosition: false });
            return false;
          }
        },

        markPositionCleared: (position, cleared) => {
          set((state) => ({
            userClearedPositions: {
              ...state.userClearedPositions,
              [position]: cleared,
            },
          }));
        },

        saveCurrentPosition: () => {
          const {
            canvas,
            activePosition,
            masterPositions,
            isSwitchingPosition,
          } = get();

          if (!canvas || isSwitchingPosition) return;

          // Single source of truth for print context
          const printContextFromProductStore =
            useProductSelectionStore.getState().activePrintContext;
          if (!printContextFromProductStore) {
            // No calibrated template is active: there is no area the
            // objects could be measured on, so nothing is written.
            logger.warn(
              'CanvasStateManager',
              `Skipping saveCurrentPosition for ${activePosition}: no print context`
            );
            return;
          }

          const offset = {
            x: printContextFromProductStore.offsetX,
            y: printContextFromProductStore.offsetY,
          };

          const json = serialize(canvas, {
            filterSystemLayers: true,
            offset,
          });

          const serializedCount = json.objects?.length ?? 0;
          const storedCount =
            masterPositions[activePosition]?.json?.objects?.length ?? 0;
          if (
            serializedCount === 0 &&
            storedCount > 0 &&
            !get().userClearedPositions[activePosition]
          ) {
            // An empty canvas without a user delete is a restore that has not
            // happened (yet) or a disposed canvas — never let it replace saved
            // objects (see analysis 2026-09-02-002, Befund G).
            logger.warn(
              'CanvasStateManager',
              `Skipping saveCurrentPosition for ${activePosition}: canvas is empty, stored state has ${storedCount} objects`
            );
            return;
          }

          const existingState = masterPositions[activePosition];
          const neverDesigned =
            serializedCount === 0 &&
            (existingState?.json ?? null) === null &&
            !get().userClearedPositions[activePosition];
          if (neverDesigned) {
            // A visited-but-untouched position must not become
            // `{objects: []}`: getAllPositionData() would then hand the slug
            // to buildEnrichedProductConfig, which persists it for every
            // product carrying that slug (analysis 2026-09-02-004).
            logger.debug(
              'CanvasStateManager',
              `Skipping saveCurrentPosition for ${activePosition}: never designed`
            );
            return;
          }

          const preview = hasDesignObjects(canvas)
            ? toDataURL(canvas, {
                printAreaOnly: true,
                printArea: {
                  x: printContextFromProductStore.offsetX,
                  y: printContextFromProductStore.offsetY,
                  width: printContextFromProductStore.widthPx,
                  height: printContextFromProductStore.heightPx,
                },
              })
            : null;

          // Preserve existing productPositionId
          const existingPositionId =
            masterPositions[activePosition]?.productPositionId ?? null;

          // Extract design adaptation data (sync with preview generation)
          const adaptationData = extractDesignAdaptationData(
            canvas,
            printContextFromProductStore
          );

          set({
            masterPositions: {
              ...masterPositions,
              [activePosition]: {
                json,
                preview,
                relativeState: adaptationData.relativeState,
                designCropUrl:
                  masterPositions[activePosition]?.designCropUrl ?? null,
                designCropDataUrl: adaptationData.designCropDataUrl,
                lastUploadedCropHash:
                  masterPositions[activePosition]?.lastUploadedCropHash ?? null,
                lastPersistedCropHash:
                  masterPositions[activePosition]?.lastPersistedCropHash ??
                  null,
                lastModified: Date.now(),
                printContext: printContextFromProductStore,
                productPositionId: existingPositionId,
                // Authoring intent is not geometry; a canvas save keeps it.
                placementPresetId:
                  masterPositions[activePosition]?.placementPresetId ?? null,
                designWidthMm:
                  masterPositions[activePosition]?.designWidthMm ?? null,
              },
            },
            userClearedPositions:
              serializedCount > 0
                ? { ...get().userClearedPositions, [activePosition]: false }
                : get().userClearedPositions,
          });

          logger.debug(
            'CanvasStateManager',
            `Saved position: ${activePosition} with offset (${offset.x}, ${offset.y})`
          );
        },

        loadSavedPosition: async (position, printContext) => {
          const { canvas, masterPositions } = get();

          if (!canvas) {
            logger.warn(
              'CanvasStateManager',
              'Cannot load saved position: no canvas'
            );
            return false;
          }

          // loadFromDesign owns the canvas while the switch lock is held; it
          // restores the freshly loaded data itself. Restoring the persisted
          // store state on top would duplicate every object.
          if (get().isSwitchingPosition) {
            logger.debug(
              'CanvasStateManager',
              'Skipping loadSavedPosition: a design load holds the switch lock',
              { position }
            );
            return false;
          }

          const positionState = masterPositions[position];

          // Check if there's saved data to load
          if (
            !positionState?.json ||
            !positionState.json.objects ||
            positionState.json.objects.length === 0
          ) {
            logger.debug(
              'CanvasStateManager',
              'No saved data to load for position',
              { position }
            );
            return false;
          }

          // Skip if canvas already has user objects (prevents duplication
          // when multiple code paths try to restore the same position).
          const existingUserObjects = getUserObjects(canvas);
          if (existingUserObjects.length > 0) {
            logger.debug(
              'CanvasStateManager',
              `Skipping loadSavedPosition: canvas already has ${existingUserObjects.length} user objects`,
              { position }
            );
            return false;
          }

          // Pinned here: the guard above narrowed json/objects, but that
          // narrowing does not survive into the async closure below.
          const savedObjectCount = positionState.json.objects.length;
          const savedJson = positionState.json;

          logger.debug(
            'CanvasStateManager',
            `Loading saved position: ${position}`,
            {
              objectCount: savedObjectCount,
              storedPrintContext: positionState.printContext,
              newPrintContext: printContext,
            }
          );

          // A restore is only valid for the canvas and the slug it started on.
          // The canvas store's own activePosition is the reference here: the
          // caller may legitimately pass a different slug right after a design
          // load, and comparing against it would discard valid restores.
          const activeAtStart = get().activePosition;
          const isRestoreStale = (): boolean =>
            get().canvas !== canvas || get().activePosition !== activeAtStart;

          const runRestore = async (): Promise<boolean> => {
            try {
              // Use stored PrintContext offset for correct coordinate transformation
              const storedContext = positionState.printContext;
              const loadOffset = {
                x: storedContext.offsetX,
                y: storedContext.offsetY,
              };

              // Clear existing user objects first
              removeUserObjects(canvas);

              // Load the saved objects
              await deserializeUserObjects(savedJson, canvas, loadOffset, {
                isStale: isRestoreStale,
              });

              if (isRestoreStale()) {
                logger.debug(
                  'CanvasStateManager',
                  'Restore discarded: position or canvas changed',
                  { position }
                );
                return false;
              }

              // Apply clipPath with current printContext (for visual clipping)
              applyClipPathToUserObjects(canvas, {
                x: printContext.offsetX,
                y: printContext.offsetY,
                width: printContext.widthPx,
                height: printContext.heightPx,
              });

              canvas.requestRenderAll();

              logger.debug(
                'CanvasStateManager',
                `Loaded ${savedObjectCount} objects for position: ${position}`
              );

              return true;
            } catch (error) {
              logger.error(
                'CanvasStateManager',
                'Failed to load saved position:',
                error
              );
              return false;
            }
          };

          // Register the restore so a concurrent loadFromDesign can await it
          // instead of writing its own objects onto a half-restored canvas.
          const restore = runRestore();
          set({ pendingRestore: restore });
          return restore.finally(() => {
            if (get().pendingRestore === restore) {
              set({ pendingRestore: null });
            }
          });
        },

        updatePreview: (position) => {
          const {
            canvas,
            masterPositions,
            activePrintContext,
            activePosition,
          } = get();

          if (!canvas || position !== activePosition) return;

          const storedContext =
            masterPositions[position]?.printContext ?? activePrintContext;
          if (!storedContext) return;

          // Re-sync offsets from the live product selection store so extraction
          // matches CanvasEditor print area (createCanvasConfig offsets).
          const context =
            activePrintContext &&
            storedContext.widthPx === activePrintContext.widthPx &&
            storedContext.heightPx === activePrintContext.heightPx
              ? {
                  ...storedContext,
                  offsetX: activePrintContext.offsetX,
                  offsetY: activePrintContext.offsetY,
                  widthMm: activePrintContext.widthMm,
                  heightMm: activePrintContext.heightMm,
                  pxPerMm: activePrintContext.pxPerMm,
                }
              : storedContext;

          let preview: string | null = null;
          if (hasDesignObjects(canvas)) {
            try {
              const dataUrl = toDataURL(canvas, {
                printAreaOnly: true,
                printArea: {
                  x: context.offsetX,
                  y: context.offsetY,
                  width: context.widthPx,
                  height: context.heightPx,
                },
              });
              // Only set preview if we got a valid data URL (not empty string)
              preview = dataUrl || null;
            } catch (error) {
              // Silently handle errors - preview will remain null
              logger.warn(
                'CanvasStateManager',
                `Failed to generate preview for position ${position}`,
                error instanceof Error ? error : String(error)
              );
            }
          }

          // Extract design adaptation data (synchronized with preview update)
          const adaptationData = extractDesignAdaptationData(canvas, context);

          const existing = masterPositions[position];
          const baseState: PositionState =
            existing ??
            createUnmaterializedPositionState(
              context,
              useProductSelectionStore.getState().activePositionId ?? null
            );

          set({
            masterPositions: {
              ...masterPositions,
              [position]: {
                ...baseState,
                preview,
                relativeState: adaptationData.relativeState,
                designCropDataUrl: adaptationData.designCropDataUrl,
                printContext: context,
                lastModified: Date.now(),
              },
            },
          });
        },

        getPreview: (position) => {
          return get().masterPositions[position]?.preview ?? null;
        },

        hasDesignForPosition: (position) => {
          const state = get().masterPositions[position];
          if (!state?.json) return false;
          return (
            Array.isArray(state.json.objects) && state.json.objects.length > 0
          );
        },

        getPositionsWithDesigns: () => {
          const { masterPositions } = get();
          return Object.entries(masterPositions)
            .filter(([, state]) => {
              if (!state.json) return false;
              return (
                Array.isArray(state.json.objects) &&
                state.json.objects.length > 0
              );
            })
            .map(([position]) => position);
        },

        writePositionFromPlacement: ({
          position,
          placementMm,
          designCropUrl,
          source,
          printContext,
          productPositionId,
          placementPresetId = null,
          designWidthMm = null,
          objectProps = {},
        }) => {
          // The stored format is synthesized (millimetres, no clip) and
          // converted to the template's canvas pixels like a loaded row;
          // objects in masterPositions live in print-area coordinates.
          const synthesized = canvasJSONFromPlacementMm(
            placementMm,
            designCropUrl,
            source,
            objectProps
          );
          const json = canvasJsonFromMm(
            { ...synthesized },
            printContext.pxPerMm
          ) as unknown as CanvasJSON;
          const relativeState = placementMmToRelative(placementMm, {
            widthMm: printContext.widthMm,
            heightMm: printContext.heightMm,
          });

          const { masterPositions } = get();
          set({
            masterPositions: {
              ...masterPositions,
              [position]: {
                json,
                preview: null,
                relativeState,
                designCropUrl,
                // No canvas ran, so there is no fresh local crop to prefer.
                designCropDataUrl: null,
                lastUploadedCropHash: null,
                lastPersistedCropHash: null,
                lastModified: Date.now(),
                printContext,
                productPositionId,
                placementPresetId,
                designWidthMm,
              },
            },
            // Same reset saveCurrentPosition performs: the position holds
            // objects again, so the empty-canvas guard must protect them.
            userClearedPositions: {
              ...get().userClearedPositions,
              [position]: false,
            },
          });
        },

        setPositionAuthoring: (position, authoring) => {
          const { masterPositions } = get();
          const existing = masterPositions[position];
          if (!existing) return;
          if (
            (existing.placementPresetId ?? null) ===
              authoring.placementPresetId &&
            (existing.designWidthMm ?? null) === authoring.designWidthMm
          ) {
            return;
          }
          set({
            masterPositions: {
              ...masterPositions,
              [position]: { ...existing, ...authoring },
            },
          });
        },

        clearPosition: (position) => {
          const { masterPositions, switchAnchors } = get();
          const newPositions = { ...masterPositions };
          delete newPositions[position];
          const newAnchors = { ...switchAnchors };
          delete newAnchors[position];
          set({ masterPositions: newPositions, switchAnchors: newAnchors });
        },

        clearAllPositions: () => {
          // switchAnchors survive on purpose: loadDesign clears positions
          // before reloading them (e.g. on a step change), and the anchor
          // must outlive that round trip. A stale anchor is ignored by the
          // signature check on the next switch.
          set({
            masterPositions: {},
            placementsByProduct: {},
            productDesignCrops: {},
          });
        },

        clearSwitchAnchors: () => {
          set({ switchAnchors: {}, masterProductId: null });
        },

        setMasterProduct: (productId) => {
          set({ masterProductId: productId });
        },

        // ═══════════════════════════════════════════════════════════════════
        // HISTORY
        // ═══════════════════════════════════════════════════════════════════

        saveToHistory: (action) => {
          const {
            canvas,
            isRestoring,
            undoStack,
            maxHistory,
            activePosition,
            activePrintContext,
          } = get();

          if (!canvas || isRestoring || !activePrintContext) return;

          const offset = {
            x: activePrintContext.offsetX,
            y: activePrintContext.offsetY,
          };

          const json = serialize(canvas, {
            filterSystemLayers: true,
            offset,
          });

          const entry: HistoryEntry = {
            id: crypto.randomUUID(),
            json,
            timestamp: Date.now(),
            action,
            position: activePosition,
          };

          const newUndoStack = [...undoStack, entry];

          // Limit stack size
          if (newUndoStack.length > maxHistory) {
            newUndoStack.shift();
          }

          // Per-product overrides (productDesignCrops, placementsByProduct)
          // survive master edits on purpose: a product the user adjusted in
          // the Products step keeps its placement until the user explicitly
          // reverts it (ConfirmApplyModal in ProductGrid).

          set({
            undoStack: newUndoStack,
            redoStack: [], // Clear redo on new action
          });

          logger.debug('CanvasStateManager', `History saved: ${action}`);
        },

        undo: async () => {
          const {
            canvas,
            undoStack,
            redoStack,
            activePosition,
            activePrintContext,
          } = get();

          if (!canvas || undoStack.length < 2 || !activePrintContext) return;

          set({ isRestoring: true });

          try {
            const currentState = undoStack[undoStack.length - 1];
            const previousState = undoStack[undoStack.length - 2];
            if (!currentState || !previousState) return;

            const newRedoStack = [...redoStack, currentState];
            const newUndoStack = undoStack.slice(0, -1);

            // Clear user objects
            removeUserObjects(canvas);

            // Load previous state
            const offset = {
              x: activePrintContext.offsetX,
              y: activePrintContext.offsetY,
            };
            await deserializeUserObjects(previousState.json, canvas, offset);

            // Apply clipPath
            applyClipPathToUserObjects(canvas, {
              x: activePrintContext.offsetX,
              y: activePrintContext.offsetY,
              width: activePrintContext.widthPx,
              height: activePrintContext.heightPx,
            });

            canvas.requestRenderAll();

            set({
              undoStack: newUndoStack,
              redoStack: newRedoStack,
              isRestoring: false,
              // Undo is a user action: an undone state without objects is a
              // deliberately empty canvas and must be persistable.
              userClearedPositions: {
                ...get().userClearedPositions,
                [activePosition]:
                  (previousState.json.objects?.length ?? 0) === 0,
              },
            });

            // Update preview
            get().updatePreview(activePosition);

            logger.debug('CanvasStateManager', 'Undo successful');
          } catch (error) {
            logger.error('CanvasStateManager', 'Undo failed:', error);
            set({ isRestoring: false });
          }
        },

        redo: async () => {
          const {
            canvas,
            undoStack,
            redoStack,
            activePosition,
            activePrintContext,
          } = get();

          if (!canvas || redoStack.length === 0 || !activePrintContext) return;

          set({ isRestoring: true });

          try {
            const stateToRestore = redoStack[redoStack.length - 1];
            if (!stateToRestore) return;

            const newUndoStack = [...undoStack, stateToRestore];
            const newRedoStack = redoStack.slice(0, -1);

            // Clear user objects
            removeUserObjects(canvas);

            // Load state
            const offset = {
              x: activePrintContext.offsetX,
              y: activePrintContext.offsetY,
            };
            await deserializeUserObjects(stateToRestore.json, canvas, offset);

            // Apply clipPath
            applyClipPathToUserObjects(canvas, {
              x: activePrintContext.offsetX,
              y: activePrintContext.offsetY,
              width: activePrintContext.widthPx,
              height: activePrintContext.heightPx,
            });

            canvas.requestRenderAll();

            set({
              undoStack: newUndoStack,
              redoStack: newRedoStack,
              isRestoring: false,
              // Redo is a user action, same rule as undo.
              userClearedPositions: {
                ...get().userClearedPositions,
                [activePosition]:
                  (stateToRestore.json.objects?.length ?? 0) === 0,
              },
            });

            // Update preview
            get().updatePreview(activePosition);

            logger.debug('CanvasStateManager', 'Redo successful');
          } catch (error) {
            logger.error('CanvasStateManager', 'Redo failed:', error);
            set({ isRestoring: false });
          }
        },

        clearHistory: () => {
          set({ undoStack: [], redoStack: [] });
        },

        // ═══════════════════════════════════════════════════════════════════
        // DESIGN LOADING
        // ═══════════════════════════════════════════════════════════════════

        loadFromDesign: async (positionData) => {
          const { canvas } = get();
          const newPositions: Record<PositionKey, PositionState> = {};

          // When loading from a design, always reset to 'front' position
          // This ensures consistency and prevents loading wrong position from persisted state
          const positionToLoad = 'front' as PositionKey;

          // Restoring the canvas is async. A switchPosition fired meanwhile
          // (CanvasEditor's print-area effect on remount) would interleave
          // with the restore: objects land at a stale offset and the store
          // ends up half old, half new. Hold the switch lock until done and
          // reconcile with the focused product afterwards.
          //
          // A remount restore (loadSavedPosition) may still be writing to the
          // canvas. Let it finish; we replace its objects right after.
          const pending = get().pendingRestore;
          if (pending) {
            await pending.catch(() => false);
          }

          // A load is never a user delete: drop any stale cleared flags.
          set({ isSwitchingPosition: true, userClearedPositions: {} });
          try {
            for (const pos of positionData) {
              if (pos.canvasData) {
                const context = createPrintContext(pos.printContext);

                // The relative view of the loaded objects, from their JSON
                // (R35); null only for a JSON without user objects.
                const relState: RelativeDesignState | null =
                  computeRelativeDesignStateFromJSON(
                    pos.canvasData as { objects?: CanvasObjectJSON[] },
                    {
                      widthPx: context.widthPx,
                      heightPx: context.heightPx,
                      offsetX: 0,
                      offsetY: 0,
                    }
                  ) ?? null;

                newPositions[pos.positionSlug] = {
                  json: pos.canvasData as CanvasJSON,
                  preview: null, // Will be generated when position is loaded
                  relativeState: relState, // Sofort verfügbar (DB oder JSON-Berechnung)
                  designCropUrl: pos.designCropUrl ?? null, // S3 URL from DB (Stufe 4.3)
                  placementPresetId: pos.placementPresetId ?? null,
                  designWidthMm: pos.designWidthMm ?? null,
                  designCropDataUrl: null, // Fresh from canvas (not available on load)
                  lastUploadedCropHash: null, // Will be set on first upload
                  lastPersistedCropHash: null, // Synced with DB state
                  lastModified: Date.now(),
                  printContext: context,
                  productPositionId: pos.productPositionId ?? null,
                };
              }
            }

            set({
              masterPositions: newPositions,
              activePosition: positionToLoad, // Always set to 'front' when loading from design
            });
            logger.info(
              'CanvasStateManager',
              `Loaded ${positionData.length} masterPositions from design, resetting to 'front'`
            );

            // If canvas is available, load the 'front' position immediately
            // This uses the PrintContext from the saved position data, avoiding race conditions
            // IMPORTANT: When loading from a design, always load 'front' position first
            // to avoid loading the wrong position from persisted state
            if (canvas) {
              // Always load 'front' position when loading from design to ensure consistency
              // This prevents loading 'back' position from persisted state on page reload
              const positionState = newPositions[positionToLoad];

              if (
                positionState?.json &&
                positionState.json.objects &&
                positionState.json.objects.length > 0
              ) {
                const offset = {
                  x: positionState.printContext.offsetX,
                  y: positionState.printContext.offsetY,
                };

                logger.debug(
                  'CanvasStateManager',
                  `Auto-loading position ${positionToLoad} with offset (${offset.x}, ${offset.y})`
                );

                // Clear existing user objects
                removeUserObjects(canvas);

                // Load with correct offset from saved data
                await deserializeUserObjects(
                  positionState.json,
                  canvas,
                  offset
                );

                // Apply clipPath
                applyClipPathToUserObjects(canvas, {
                  x: positionState.printContext.offsetX,
                  y: positionState.printContext.offsetY,
                  width: positionState.printContext.widthPx,
                  height: positionState.printContext.heightPx,
                });

                canvas.requestRenderAll();

                // Update activePrintContext to match loaded position
                set({ activePrintContext: positionState.printContext });

                // Generate preview for the active (front) position immediately
                const frontPreview = hasDesignObjects(canvas)
                  ? toDataURL(canvas, {
                      printAreaOnly: true,
                      printArea: {
                        x: positionState.printContext.offsetX,
                        y: positionState.printContext.offsetY,
                        width: positionState.printContext.widthPx,
                        height: positionState.printContext.heightPx,
                      },
                    }) || null
                  : null;
                const frontAdaptation = extractDesignAdaptationData(
                  canvas,
                  positionState.printContext
                );

                // Update front position with preview
                const updatedPositions = { ...get().masterPositions };
                const currentFrontPos = updatedPositions[positionToLoad];
                if (currentFrontPos) {
                  updatedPositions[positionToLoad] = {
                    ...currentFrontPos,
                    preview: frontPreview,
                    relativeState: frontAdaptation.relativeState,
                    designCropDataUrl: frontAdaptation.designCropDataUrl,
                  };
                }

                // Generate previews for non-active masterPositions by temporarily loading them
                for (const [slug, posState] of Object.entries(newPositions)) {
                  if (
                    slug === positionToLoad ||
                    !posState.json?.objects?.length
                  ) {
                    continue;
                  }

                  try {
                    const posOffset = {
                      x: posState.printContext.offsetX,
                      y: posState.printContext.offsetY,
                    };

                    // Temporarily load this position's objects
                    removeUserObjects(canvas);
                    await deserializeUserObjects(
                      posState.json,
                      canvas,
                      posOffset
                    );
                    applyClipPathToUserObjects(canvas, {
                      x: posState.printContext.offsetX,
                      y: posState.printContext.offsetY,
                      width: posState.printContext.widthPx,
                      height: posState.printContext.heightPx,
                    });
                    canvas.requestRenderAll();

                    // Capture preview
                    const preview = hasDesignObjects(canvas)
                      ? toDataURL(canvas, {
                          printAreaOnly: true,
                          printArea: {
                            x: posState.printContext.offsetX,
                            y: posState.printContext.offsetY,
                            width: posState.printContext.widthPx,
                            height: posState.printContext.heightPx,
                          },
                        }) || null
                      : null;
                    const adaptation = extractDesignAdaptationData(
                      canvas,
                      posState.printContext
                    );

                    const currentSlugPos = updatedPositions[slug];
                    if (currentSlugPos) {
                      updatedPositions[slug] = {
                        ...currentSlugPos,
                        preview,
                        relativeState: adaptation.relativeState,
                        designCropDataUrl: adaptation.designCropDataUrl,
                      };
                    }
                  } catch (err) {
                    logger.warn(
                      'CanvasStateManager',
                      `Failed to generate preview for position ${slug}`,
                      err instanceof Error ? err : String(err)
                    );
                  }
                }

                // Restore the active (front) position on canvas
                removeUserObjects(canvas);
                await deserializeUserObjects(positionState.json, canvas, {
                  x: positionState.printContext.offsetX,
                  y: positionState.printContext.offsetY,
                });
                applyClipPathToUserObjects(canvas, {
                  x: positionState.printContext.offsetX,
                  y: positionState.printContext.offsetY,
                  width: positionState.printContext.widthPx,
                  height: positionState.printContext.heightPx,
                });
                canvas.requestRenderAll();

                // Commit the preview fields onto the LIVE state. A same-position
                // switch may have updated json/printContext while we
                // awaited; a wholesale write of the snapshot would clobber it.
                set((state) => {
                  const merged = { ...state.masterPositions };
                  for (const [slug, pos] of Object.entries(updatedPositions)) {
                    const live = merged[slug];
                    merged[slug] = live
                      ? {
                          ...live,
                          preview: pos.preview,
                          relativeState: pos.relativeState,
                          designCropDataUrl: pos.designCropDataUrl,
                        }
                      : pos;
                  }
                  return { masterPositions: merged };
                });

                logger.info(
                  'CanvasStateManager',
                  `Generated previews for ${Object.keys(updatedPositions).length} masterPositions after design load`
                );

                // Save to history
                get().saveToHistory('load');
              }
            }
          } finally {
            set({ isSwitchingPosition: false });
          }
        },

        getAllPositionData: (options = {}) => {
          const { masterPositions, switchAnchors, masterProductId } = get();
          const resolveMaster = options.resolveMaster ?? true;
          const result: Record<
            PositionKey,
            {
              json: CanvasJSON | null;
              printContext: PrintContext;
              relativeState: RelativeDesignState | null;
              designCropUrl: string | null;
              placementPresetId: string | null;
              designWidthMm: number | null;
            }
          > = {};

          for (const [position, state] of Object.entries(masterPositions)) {
            // Only include masterPositions that have canvas data
            if (state.json !== null) {
              // The master state (KTD8): when this position is shown on
              // another product's print area, hand back the anchor's state
              // in the anchor's coordinate space, so canvasData,
              // relativeState and printContext describe the master
              // area. Without an anchor the live state on its own context
              // is the master state.
              let json = state.json;
              let printContext = state.printContext;
              let relativeState = state.relativeState;
              const stored = switchAnchors[position];
              if (resolveMaster && stored?.rel) {
                const objects = (json.objects ?? []) as CanvasObjectJSON[];
                const anchor = applyResolvedAnchor(
                  resolveAnchorFor(
                    objects,
                    state.printContext,
                    stored,
                    masterProductId
                  ),
                  position,
                  get()
                );
                if (anchor && !isAnchorHome(anchor, state.printContext)) {
                  json = {
                    ...json,
                    objects: rescaleCanvasObjectsJSON(
                      objects,
                      areaFromPrintContext(state.printContext),
                      areaFromPrintContext(anchor.printContext),
                      anchor.rel
                    ) as CanvasJSON['objects'],
                  };
                  printContext = anchor.printContext;
                  relativeState = anchor.rel;
                }
              }
              result[position] = {
                json,
                printContext,
                relativeState,
                // Stufe 4.3: Only return S3 URLs to backend (NOT DataURLs).
                // DataURL → S3 upload happens in uploadDesignCrops() before save.
                designCropUrl: state.designCropUrl,
                placementPresetId: state.placementPresetId ?? null,
                designWidthMm: state.designWidthMm ?? null,
              };
            }
          }

          return result;
        },

        // ═══════════════════════════════════════════════════════════════════
        // CROP UPLOAD STATE (Stufe 4.3)
        // ═══════════════════════════════════════════════════════════════════

        setCropUploadResult: (
          positionSlug: string,
          s3Url: string,
          contentHash: string
        ) => {
          const { masterPositions } = get();
          const pos = masterPositions[positionSlug];
          if (!pos) return;

          // IMPORTANT: Do NOT clear designCropDataUrl here!
          // The DataURL must survive until saveDesignConfiguration succeeds
          // (2-phase commit). If upload succeeds but persist fails, we need
          // the DataURL to retry. Clearing happens in setCropPersisted().
          set({
            masterPositions: {
              ...masterPositions,
              [positionSlug]: {
                ...pos,
                designCropUrl: s3Url, // S3 URL for backend persistence
                lastUploadedCropHash: contentHash,
                // designCropDataUrl intentionally kept — cleared in setCropPersisted()
              },
            },
          });

          logger.debug(
            'CanvasStateManager',
            `Crop upload result set for ${positionSlug}: ${s3Url} (hash: ${contentHash})`
          );
        },

        setCropPersisted: (positionSlug: string, contentHash: string) => {
          const { masterPositions } = get();
          const pos = masterPositions[positionSlug];
          if (!pos) return;

          // 2-phase commit complete: DB now has the S3 URL.
          // Safe to clear the runtime DataURL — S3 URL is the canonical source.
          set({
            masterPositions: {
              ...masterPositions,
              [positionSlug]: {
                ...pos,
                lastPersistedCropHash: contentHash,
                designCropDataUrl: null, // Safe to clear NOW (DB has the S3 URL)
              },
            },
          });
        },

        // ═══════════════════════════════════════════════════════════════════
        // PER-PRODUCT PLACEMENTS (Stufe 5+)
        // ═══════════════════════════════════════════════════════════════════

        switchProduct: (productId: string) => {
          set({ activeProductId: productId });
          logger.debug(
            'CanvasStateManager',
            `Switched active product to ${productId}`
          );
        },

        setProductPlacement: (
          productId: string,
          slug: PositionKey,
          placement: RelativeDesignState | null
        ) => {
          const { placementsByProduct, productDesignCrops } = get();

          if (placement === null) {
            // A reset must drop both override maps: consumers evaluate the
            // independent crop first, so a leftover crop would keep the
            // product marked as custom although the placement is gone.
            set({
              placementsByProduct: removeProductPosition(
                placementsByProduct,
                productId,
                slug
              ),
              productDesignCrops: removeProductPosition(
                productDesignCrops,
                productId,
                slug
              ),
            });
          } else {
            set({
              placementsByProduct: {
                ...placementsByProduct,
                [productId]: {
                  ...(placementsByProduct[productId] ?? {}),
                  [slug]: placement,
                },
              },
            });
          }

          logger.debug(
            'CanvasStateManager',
            `Set product placement for product ${productId}, position ${slug}`,
            placement
          );
        },

        clearProductOverrides: (productId) => {
          const { productDesignCrops, placementsByProduct } = get();
          const nextCrops = { ...productDesignCrops };
          const nextPlacements = { ...placementsByProduct };
          delete nextCrops[productId];
          delete nextPlacements[productId];
          set({
            productDesignCrops: nextCrops,
            placementsByProduct: nextPlacements,
          });
          logger.debug(
            'CanvasStateManager',
            `Cleared overrides for product ${productId}`
          );
        },

        getProductPlacement: (
          productId: string,
          slug: PositionKey
        ): RelativeDesignState | undefined => {
          return get().placementsByProduct[productId]?.[slug];
        },

        hydrateProductPlacements: (products) => {
          const hydrated: Record<
            string,
            Record<PositionKey, RelativeDesignState>
          > = {};

          for (const product of products) {
            const bySlug: Record<PositionKey, RelativeDesignState> = {};

            for (const pos of product.positions) {
              // Only custom placements (user-adjusted) are hydrated; rows
              // without one derive from the master.
              if (pos.isCustomPlacement && pos.placement) {
                bySlug[pos.slug] = pos.placement;
              }
            }

            if (Object.keys(bySlug).length > 0) {
              hydrated[product.productId] = bySlug;
            }
          }

          set({ placementsByProduct: hydrated });
        },

        setProductDesignCrop: (productId, slug, crop) => {
          const { productDesignCrops } = get();
          const productCrops = {
            ...(productDesignCrops[productId] ?? {}),
            [slug]: crop,
          };
          set({
            productDesignCrops: {
              ...productDesignCrops,
              [productId]: productCrops,
            },
          });
        },

        setAllProductDesignCrops: (crops) => {
          set({ productDesignCrops: crops });
        },

        getActiveMasterPosition: (): PositionState | null => {
          const { masterPositions, activePosition } = get();
          return masterPositions[activePosition] ?? null;
        },

        // ═══════════════════════════════════════════════════════════════════
        // RESET
        // ═══════════════════════════════════════════════════════════════════

        reset: () => {
          set({
            ...initialState,
            canvas: get().canvas, // Keep canvas reference
          });
          logger.debug('CanvasStateManager', 'Store reset');
        },
      }),
      {
        name: 'canvas-state-manager',
        // sessionStorage in an impersonation tab, localStorage otherwise.
        storage: persistStorage(),
        version: 5,
        migrate: (persisted, version) => {
          const state = persisted as Record<string, unknown>;
          if (version < 5) {
            // Before Release 2 every persisted context and anchor was
            // measured without the template's Pixel Scale; designs of that
            // time are discarded, so nothing of them is carried over.
            return {
              activePosition: state.activePosition ?? 'front',
              activeProductId: null,
              masterProductId: null,
              masterPositions: {},
              switchAnchors: {},
              placementsByProduct: {},
              productDesignCrops: {},
            };
          }
          return state;
        },
        partialize: (state) => ({
          masterPositions: state.masterPositions,
          switchAnchors: state.switchAnchors,
          activePosition: state.activePosition,
          activeProductId: state.activeProductId,
          masterProductId: state.masterProductId,
          placementsByProduct: state.placementsByProduct,
          productDesignCrops: state.productDesignCrops,
        }),
      }
    ),
    { name: 'CanvasStateManager' }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

/** Check if undo is available */
export const useCanUndo = () =>
  useCanvasStateManager((state) => state.undoStack.length >= 2);

/** Check if redo is available */
export const useCanRedo = () =>
  useCanvasStateManager((state) => state.redoStack.length > 0);

/** Get undo stack count */
export const useUndoCount = () =>
  useCanvasStateManager((state) => state.undoStack.length);

/** Get redo stack count */
export const useRedoCount = () =>
  useCanvasStateManager((state) => state.redoStack.length);

/** Get preview for a specific position */
export const usePositionPreview = (position: PositionKey) =>
  useCanvasStateManager(
    (state) => state.masterPositions[position]?.preview ?? null
  );

/**
 * Get all master positions state - returns stable reference to positions object.
 * Use this with useMemo in consumer to derive previews.
 *
 * @example
 * const positions = usePositions();
 * const previews = useMemo(() => {
 *   const result: Record<string, string | null> = {};
 *   for (const [key, val] of Object.entries(positions)) {
 *     result[key] = val.preview;
 *   }
 *   return result;
 * }, [positions]);
 */
export const usePositions = () =>
  useCanvasStateManager(useShallow((state) => state.masterPositions));

/**
 * @deprecated Use usePositions() + useMemo instead to avoid infinite loops.
 * This selector creates a new object on every call which can cause issues
 * with React's useSyncExternalStore.
 */
export const useAllPositionPreviews = () =>
  useCanvasStateManager(useShallow((state) => state.masterPositions));

/** Check if a position has design */
export const useHasDesignForPosition = (position: PositionKey) =>
  useCanvasStateManager((state) => {
    const posState = state.masterPositions[position];
    if (!posState?.json) return false;
    return (
      Array.isArray(posState.json.objects) && posState.json.objects.length > 0
    );
  });

/** Check if currently switching positions */
export const useIsSwitchingPosition = () =>
  useCanvasStateManager((state) => state.isSwitchingPosition);

/** Get active position */
export const useActivePosition = () =>
  useCanvasStateManager((state) => state.activePosition);

/** Get active product ID (Stufe 5) */
export const useActiveProductId = () =>
  useCanvasStateManager((state) => state.activeProductId);

/** Get per-product placements (Stufe 5+) */
export const usePlacementsByProduct = () =>
  useCanvasStateManager(useShallow((state) => state.placementsByProduct));

/** Get per-product design crops */
export const useProductDesignCrops = () =>
  useCanvasStateManager(useShallow((state) => state.productDesignCrops));

/** Get active PrintContext */
export const useActivePrintContext = () =>
  useCanvasStateManager((state) => state.activePrintContext);
